import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ImportExportService from '../Services/importExport.service';

/**
 * Custom hook for Taiodet import functionality
 * @param {Object} options - Configuration options
 * @param {Object} options.client - Client object with caseId and accountantId
 * @param {string} options.token - Authentication token
 * @param {Function} options.fetchEnums - Function to refresh enums after import
 * @param {Function} options.setLoading - Function to set loading state
 * @returns {Object} Object containing preview and commit functions for account cards and journal entries
 */
const useTaiodetImport = ({ client, token, fetchEnums, setLoading }) => {
    const { t } = useTranslation();

    // Account Cards
    const previewTaiodetAccountCards = useCallback(async (file) => {
        if (!file || !client) return;
        try {
            const res = await ImportExportService.PreviewTaiodetAccountCards(
                client.caseId,
                client.accountantId,
                file,
                token
            );
            toast.success(t('PreviewSuccess'));
            return res;
        } catch (err) {
            console.error('Taiodet account cards preview failed:', err);
            toast.error(t('PreviewFailed'));
            throw err;
        }
    }, [client, token, t]);

    const commitTaiodetAccountCards = useCallback(async (file) => {
        if (!file || !client) return;
        try {
            setLoading?.(true);
            const res = await ImportExportService.ImportTaiodetAccountCards(
                client.caseId,
                file,
                token
            );
            
            const processedCount = res?.processedCount ?? res?.[0] ?? 0;
            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Taiodet account cards import errors:', errorMessages);
                toast.warning(
                    t('TaiodetAccountCardsUploadedWithErrors', {
                        processedCount,
                        errorCount,
                        firstError: errorMessages[0]
                    }) || `Imported ${processedCount} cards with ${errorCount} errors. Check console for details.`
                );
            } else {
                toast.success(
                    t('TaiodetAccountCardsUploadedSuccessfully', {
                        processedCount
                    }) || `Successfully imported ${processedCount} account cards from Taiodet.`
                );
            }
            
            if (fetchEnums) {
                await fetchEnums(token, client, true);
            }
            return res;
        } catch (err) {
            console.error('Taiodet account cards import error:', err);
            toast.error(t('TaiodetAccountCardsUploadFailed') || 'Taiodet account cards import failed');
            throw err;
        } finally {
            setLoading?.(false);
        }
    }, [client, token, t, fetchEnums, setLoading]);

    // Journal Entries
    const previewTaiodetJournal = useCallback(async (file, date) => {
        if (!file || !client || !date) return;
        try {
            const res = await ImportExportService.PreviewTaiodetJournal(
                client.caseId,
                client.accountantId,
                file,
                date,
                token
            );
            toast.success(t('PreviewSuccess'));
            return res;
        } catch (err) {
            console.error('Taiodet journal preview failed:', err);
            toast.error(t('PreviewFailed'));
            throw err;
        }
    }, [client, token, t]);

    const commitTaiodetJournal = useCallback(async (file, date) => {
        if (!file || !client || !date) return;
        try {
            setLoading?.(true);
            const res = await ImportExportService.ImportTaiodetJournal(
                client.caseId,
                file,
                date,
                token
            );
            
            const processedCount = res?.processedCount ?? res?.[0] ?? 0;
            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Taiodet journal import errors:', errorMessages);
                toast.warning(
                    t('TaiodetJournalUploadedWithErrors', {
                        processedCount,
                        errorCount,
                        firstError: errorMessages[0]
                    }) || `Imported ${processedCount} entries with ${errorCount} errors. Check console for details.`
                );
            } else {
                toast.success(
                    t('TaiodetJournalUploadedSuccessfully', {
                        processedCount
                    }) || `Successfully imported ${processedCount} journal entries from Taiodet.`
                );
            }
            
            if (fetchEnums) {
                await fetchEnums(token, client, true);
            }
            return res;
        } catch (err) {
            console.error('Taiodet journal import error:', err);
            toast.error(t('TaiodetJournalUploadFailed') || 'Taiodet journal import failed');
            throw err;
        } finally {
            setLoading?.(false);
        }
    }, [client, token, t, fetchEnums, setLoading]);

    return {
        previewTaiodetAccountCards,
        commitTaiodetAccountCards,
        previewTaiodetJournal,
        commitTaiodetJournal
    };
};

export default useTaiodetImport;

